import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { xor } from 'lodash'
import { format } from 'date-fns'

import { DisbursementsDetailsActionsMenu } from '../DisbursementsDetailsActionsMenu/DisbursementsDetailsActionsMenu'
import { MarkAsPaidModal } from '../../markAsPaidModal/MarkAsPaidModal'
import { MarkAsApprovedOrRemoveFromBatchModal } from '../../markAsApprovedOrRemoveFromBatchModal/MarkAsApprovedOrRemoveFromBatchModal'
import { MarkAsSentModal } from '../../markAsSentModal/MarkAsSentModal'
import { RemoveFromBatchBulk } from '../../removeFromBatchBulk/RemoveFromBatchBulk'
import { MarkAsApprovedBulk } from '../../markAsApprovedBulk/MarkAsApprovedBulk'
import { MarkAsSentBulk } from '../../markAsSentBulk/MarkAsSentBulk'
import { LogReturnedPayment } from '../../logReturnedPaymentModal/LogReturnedPayment'
import { RemoveFromBatchModal } from '../../removeFromBatchModal/RemoveFromBatchModal'
import { ConfirmBeneficiaryModal } from '../../confirmBeneficiaryModal/ConfirmBeneficiaryModal'
import { TrusteeApproveBatchModal } from '../../trusteeApproveBatchModal/TrusteeApproveBatchModal'
import { AgentApproveBatchModal } from '../../agentApproveBatchModal/AgentApproveBatchModal'
import { ManagerApproveBatchModal } from '../../managerApproveBatchModal/ManagerApproveBatchModal'
import { useMultiApprovalScope } from '../hooks/useMultiApprovalScope'

import {
  Badge,
  Checkbox,
  FlagAvatar,
  Table,
  TableBody,
  TableCell,
  TableColumnSortDirection,
  TablePagination,
  TableProvider,
  TableRow,
  CardDashboard,
  Chip,
  Flex,
  Button,
  Tooltip
} from '@percent/lemonade'
import { useCurrencies, usePermissions, useOrganisationFilter } from '@percent/admin-dashboard/common/hooks'
import {
  BeneficiaryStatus,
  DisbursementBatchStatus,
  DisbursementBeneficiary,
  DisbursementEnhanced,
  DisbursementStatus,
  DisbursementType
} from '@percent/admin-dashboard/api/actions/disbursements/disbursements.types'
import { DisbursementsBreakdownProps } from './DisbursementsBreakdown.types'
import { formatAmountFromMinorUnits } from '@percent/admin-dashboard/common/utility/formatAmount/formatAmount'
import { dayJS } from '@percent/admin-dashboard/common/library/utility/date'
import {
  usePostHogFeatureFlag,
  POSTHOG_FEATURE_FLAGS
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag'
import { BulkActionMenu } from './bulkActionMenu/BulkActionMenu'
import style from './DisbursementsBreakdown.module.scss'
import { DisbursementsBreakdownFilters } from './disbursementsBreakdownFilters/DisbursementsBreakdownFilters'
import { useBeneficiaryStatusFilter } from './disbursementsBreakdownFilters/useBeneficiaryStatusFilter'
import { useStatusFilter } from './disbursementsBreakdownFilters/useStatusFilter'
import { Export } from './export/Export'

export enum DisbursementActionModalKind {
  MARK_AS_PAID = 'mark_as_paid',
  MARK_AS_APPROVED = 'mark_as_approved',
  MARK_AS_SENT = 'mark_as_sent',
  CANCEL = 'cancel',
  RETURNED_PAYMENT = 'returned_payment',
  CONFIRM_BENEFICIARY = 'confirm_beneficiary'
}

export function DisbursementsBreakdown({
  disbursementBatchId,
  data,
  previousPage,
  nextPage,
  totalResults,
  refreshData,
  queryParams,
  setQueryParams,
  foundationId,
  endDate,
  detailedCounts,
  summary,
  totals,
  totalDisbursementsCount,
  allBeneficiariesConfirmed
}: Readonly<DisbursementsBreakdownProps>) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { currencyInfo } = useCurrencies()
  const { userHasPermissions } = usePermissions()
  const disbursementsBatchPartnerFilterFeatureFlag = usePostHogFeatureFlag(
    POSTHOG_FEATURE_FLAGS.AD_DISBURSEMENTS_BATCH_PARTNER_FILTER
  )
  const disbursementDonationsViewFeatureFlag = usePostHogFeatureFlag(
    POSTHOG_FEATURE_FLAGS.AD_DISBURSEMENT_DONATIONS_VIEW
  )
  const logReturnedPaymentFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.AD_LOG_RETURNED_PAYMENT)
  const internalFgaDoraDisbursementsFeatureFlag = usePostHogFeatureFlag(
    POSTHOG_FEATURE_FLAGS.INTERNAL_FGA_DORA_DISBURSEMENTS
  )
  const disbursementCancellationReasonFeatureFlag = usePostHogFeatureFlag(
    POSTHOG_FEATURE_FLAGS.AD_DISBURSEMENT_CANCELLATION_REASON
  )
  const beneficiariesFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.AD_BENEFICIARIES)
  const multiApprovalFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.AD_MULTI_APPROVAL)

  const { isInScope: isInMultiApprovalScope, hasBankTransferDisbursements: batchHasBankTransferDisbursements } =
    useMultiApprovalScope(foundationId, detailedCounts)

  const organisationFilter = useOrganisationFilter({
    filterValues: queryParams.organisationId ?? [],
    onFilterValuesChange: (values: string[]) => {
      setQueryParams({ ...queryParams, organisationId: values })
    }
  })

  const statusFilter = useStatusFilter({
    queryParams,
    setQueryParams,
    includeMultiApprovalStatuses: isInMultiApprovalScope
  })

  const beneficiaryStatusFilter = useBeneficiaryStatusFilter({
    queryParams,
    setQueryParams
  })

  const [selectedDisbursementDetails, setSelectedDisbursementDetails] = useState<DisbursementEnhanced | undefined>(
    undefined
  )
  const [selectedDisbursementIds, setSelectedDisbursementIds] = useState<string[]>([])
  const [activeActionModal, setActiveActionModal] = useState<DisbursementActionModalKind | undefined>(undefined)
  const [isBulkRemovalModalOpen, setIsBulkRemovalModalOpen] = useState(false)
  const [isMarkAsApprovedBulkModalOpen, setIsMarkAsApprovedBulkModalOpen] = useState(false)
  const [isMarkAsSentBulkModalOpen, setIsMarkAsSentBulkModalOpen] = useState(false)
  const [isMarkAllChequesSentModalOpen, setIsMarkAllChequesSentModalOpen] = useState(false)
  const [isTrusteeApproveBatchModalOpen, setIsTrusteeApproveBatchModalOpen] = useState(false)
  const [isAgentApproveBatchModalOpen, setIsAgentApproveBatchModalOpen] = useState(false)
  const [isManagerApproveBatchModalOpen, setIsManagerApproveBatchModalOpen] = useState(false)

  const tableContainerRef = useRef<HTMLDivElement>(null)

  const pageHasInternalDisbursements = data.some(row => row.type === DisbursementType.INTERNAL)
  const hasActiveBeneficiaryStatusFilter = (queryParams.beneficiaryStatus?.length ?? 0) > 0

  /** Keep filter/column visible when the list is empty but the batch still has internal rows or a filter is applied. */
  const showBeneficiaryUI =
    beneficiariesFeatureFlag &&
    (pageHasInternalDisbursements || batchHasBankTransferDisbursements || hasActiveBeneficiaryStatusFilter)

  const isNotNullable = <T,>(value: T | null | undefined): value is T => value !== null && value !== undefined

  const allowedStatusesForBulkActions = new Set([
    DisbursementStatus.PENDING_APPROVAL,
    DisbursementStatus.PENDING_PAYMENT,
    DisbursementStatus.PENDING_TRUSTEE_APPROVAL,
    DisbursementStatus.PENDING_AGENT_APPROVAL,
    DisbursementStatus.PENDING_MANAGER_APPROVAL,
    DisbursementStatus.FAILED
  ])
  const availableStatusesOnPage = new Set(data.map(row => row.status))
  const isAllSelected = selectedDisbursementIds.length > 0 && selectedDisbursementIds.length === data.length
  const isIndeterminate = selectedDisbursementIds.length > 0 && !isAllSelected

  const canMarkAllChequesSent =
    !internalFgaDoraDisbursementsFeatureFlag || userHasPermissions('disbursements:can_mark_in_transit')
  const canApprove = !internalFgaDoraDisbursementsFeatureFlag || userHasPermissions('disbursements:can_approve')
  const canSubmitPayments =
    !internalFgaDoraDisbursementsFeatureFlag || userHasPermissions('disbursements:can_submit_payments')

  const batchApprovalCtaByStatus: Partial<
    Record<DisbursementBatchStatus, { label: string; dataTestId: string; open: () => void; canPerform: boolean }>
  > = {
    [DisbursementBatchStatus.TRUSTEE_APPROVAL_REQUIRED]: {
      label: t('button.markAllAsFoundationApproved'),
      dataTestId: 'batch-approval-cta-trustee',
      open: () => setIsTrusteeApproveBatchModalOpen(true),
      canPerform: canApprove
    },
    [DisbursementBatchStatus.AGENT_APPROVAL_REQUIRED]: {
      label: t('button.markAllAsAgentApproved'),
      dataTestId: 'batch-approval-cta-agent',
      open: () => setIsAgentApproveBatchModalOpen(true),
      canPerform: canApprove
    },
    [DisbursementBatchStatus.MANAGER_APPROVAL_REQUIRED]: {
      label: t('button.approveAndSubmitPayments'),
      dataTestId: 'batch-approval-cta-manager',
      open: () => setIsManagerApproveBatchModalOpen(true),
      canPerform: canSubmitPayments
    }
  }

  const pendingApprovalStages: {
    totalsKey: 'trusteeApprovalRequired' | 'agentApprovalRequired' | 'managerApprovalRequired'
    batchStatus: DisbursementBatchStatus
  }[] = [
    { totalsKey: 'trusteeApprovalRequired', batchStatus: DisbursementBatchStatus.TRUSTEE_APPROVAL_REQUIRED },
    { totalsKey: 'agentApprovalRequired', batchStatus: DisbursementBatchStatus.AGENT_APPROVAL_REQUIRED },
    { totalsKey: 'managerApprovalRequired', batchStatus: DisbursementBatchStatus.MANAGER_APPROVAL_REQUIRED }
  ]

  const activeApprovalStage = pendingApprovalStages.find(({ totalsKey }) => (totals?.[totalsKey] ?? 0) > 0)

  const batchApprovalCta =
    multiApprovalFeatureFlag && activeApprovalStage
      ? batchApprovalCtaByStatus[activeApprovalStage.batchStatus] ?? null
      : null

  const isDisbursementApprovalDisabled =
    activeApprovalStage?.batchStatus !== DisbursementBatchStatus.TRUSTEE_APPROVAL_REQUIRED && !allBeneficiariesConfirmed

  const getBatchApprovalDisabledTooltip = () => {
    if (batchApprovalCta && !batchApprovalCta.canPerform) {
      return t('tooltip.batchApprovalNoPermission')
    }

    if (isDisbursementApprovalDisabled) {
      return t('tooltip.batchApprovalBeneficiaryUnconfirmed')
    }

    return ''
  }

  useEffect(() => {
    setSelectedDisbursementIds([])
  }, [data])

  const getIsCheckboxDisabled = (row: DisbursementEnhanced) => {
    if (!allowedStatusesForBulkActions.has(row.status)) {
      return true
    }

    if (selectedDisbursementIds.length === 0) {
      return false
    }

    const selectedStatus = data.find(disbursement => disbursement.id === selectedDisbursementIds[0])?.status

    return row.status !== selectedStatus
  }

  const handleSelectAllByStatus = (status: DisbursementStatus) => {
    setSelectedDisbursementIds(data.filter(row => row.status === status).map(row => row.id))
  }

  const handleToggleAll = () => {
    setSelectedDisbursementIds(previous => {
      if (previous.length === data.length) {
        return []
      }

      return data.map(row => row.id)
    })
  }

  const handleToggleRow = (row: DisbursementEnhanced) => {
    setSelectedDisbursementIds(previous => xor(previous, [row.id]))
  }

  const selectAllCheckbox = (
    <div className={style.selectAllWrapper}>
      <Checkbox
        name="selectAllDisbursements"
        label=""
        variant={isIndeterminate ? 'indeterminate' : 'default'}
        active={selectedDisbursementIds.length > 0}
        onChange={handleToggleAll}
        disabled={
          availableStatusesOnPage.size > 1 ||
          Array.from(availableStatusesOnPage).some(status => !allowedStatusesForBulkActions.has(status))
        }
        aria-label="Select all disbursements"
        dataTestId="disbursements-select-all-checkbox"
      />
    </div>
  )

  const handleRequestSort = (columnKey: string) => {
    const newDirection = queryParams.sort === columnKey && queryParams.direction === 'DESC' ? 'ASC' : 'DESC'
    setQueryParams({
      ...queryParams,
      sort: columnKey,
      direction: newDirection
    })
  }

  const columns = [
    { id: 'select', isSortable: false, props: { width: '26px' }, content: selectAllCheckbox },
    { id: 'country', isSortable: false, props: { width: '72px' }, content: t('table.country') },
    { id: 'organization', isSortable: false, content: t('table.organization') },
    disbursementsBatchPartnerFilterFeatureFlag
      ? { id: 'partner', isSortable: false, props: { width: '120px' }, content: t('table.partner') }
      : null,
    { id: 'totalDonation', isSortable: false, props: { width: '140px' }, content: t('table.totalDonation') },
    { id: 'currency', isSortable: false, props: { width: '92px' }, content: t('table.currency') },
    { id: 'status', isSortable: false, props: { width: '160px' }, content: t('table.status') },
    showBeneficiaryUI
      ? {
          id: 'beneficiary',
          isSortable: false,
          props: { width: '130px' },
          content: t('table.beneficiary')
        }
      : null,
    logReturnedPaymentFeatureFlag
      ? { id: 'paymentAttempts', isSortable: false, props: { width: '100px' }, content: t('table.attempts') }
      : null,
    { id: 'updatedAt', isSortable: true, props: { width: '170px' }, content: t('typography.updatedAt') },
    { id: 'actions', isSortable: false, props: { width: '40px' }, content: ' ' }
  ].filter(isNotNullable)

  const closeModal = () => {
    setSelectedDisbursementDetails(undefined)
    setActiveActionModal(undefined)
  }

  const getBadgeVariant = (status: DisbursementStatus) => {
    switch (status) {
      case DisbursementStatus.CANCELLED:
        return (
          <Badge variant="critical" icon="reject">
            {t('status.removed')}
          </Badge>
        )
      case DisbursementStatus.PAID:
        return (
          <Badge variant="positive" icon="approve">
            {t('status.paid')}
          </Badge>
        )
      case DisbursementStatus.PENDING_APPROVAL:
        return (
          <Badge variant="informative" icon="clock">
            {t('status.pendingApproval')}
          </Badge>
        )
      case DisbursementStatus.PENDING_TRUSTEE_APPROVAL:
        return (
          <Badge variant="informative" icon="clock">
            {t('status.pendingFoundationApproval')}
          </Badge>
        )
      case DisbursementStatus.PENDING_AGENT_APPROVAL:
        return (
          <Badge variant="informative" icon="clock">
            {t('status.pendingAgentApproval')}
          </Badge>
        )
      case DisbursementStatus.PENDING_MANAGER_APPROVAL:
        return (
          <Badge variant="informative" icon="clock">
            {t('status.pendingManagerApproval')}
          </Badge>
        )
      case DisbursementStatus.SENT:
        return (
          <Badge variant="informative" icon="check-badge">
            {t('status.sent')}
          </Badge>
        )
      case DisbursementStatus.PENDING_PAYMENT:
        return (
          <Badge variant="default" icon="check-badge">
            {t('status.pendingPayment')}
          </Badge>
        )
      case DisbursementStatus.RETURNED:
        return (
          <Badge variant="critical" icon="warning">
            {t('status.paymentReturned')}
          </Badge>
        )
      case DisbursementStatus.PAYMENT_INITIATED:
        return (
          <Badge variant="informative" icon="clock">
            {t('status.paymentInitiated')}
          </Badge>
        )
      case DisbursementStatus.FAILED:
        return (
          <Badge variant="critical" icon="reject">
            {t('status.failed')}
          </Badge>
        )
      default:
        return null
    }
  }

  const getBeneficiaryBadge = (beneficiary?: DisbursementBeneficiary) => {
    const newBeneficiary = (
      <Tooltip content={`${t('typography.status')}: ${t('status.beneficiaryNew')}`}>
        <div>
          <Badge variant="warning" icon="warning" />
        </div>
      </Tooltip>
    )

    switch (beneficiary?.status) {
      case BeneficiaryStatus.PROCESSING:
        return newBeneficiary
      case BeneficiaryStatus.OUTDATED:
        return (
          <Tooltip content={`${t('typography.status')}: ${t('status.beneficiaryUpdated')}`}>
            <div>
              <Badge variant="warning" icon="warning" />
            </div>
          </Tooltip>
        )
      case BeneficiaryStatus.ACTIVE:
        return (
          <Tooltip
            content={
              <Flex direction="column" gap={4}>
                <span>
                  {t('typography.status')}: {t('status.beneficiaryConfirmed')}
                </span>
                {beneficiary?.lastUsedAt
                  ? t('typography.lastUsed', { date: format(new Date(beneficiary.lastUsedAt), 'dd MMM yyyy') })
                  : null}
              </Flex>
            }
          >
            <div>
              <Badge variant="positive" icon="approve" />
            </div>
          </Tooltip>
        )
      default:
        return newBeneficiary
    }
  }

  const approvedChequesCount = detailedCounts.find(
    ({ type, status: detailedCountStatus }) => type === 'cheque' && detailedCountStatus === 'approved'
  )?.count

  const shouldShowBatchPaymentFile = summary.some(({ status }) => status === 'approved') && approvedChequesCount

  return (
    <CardDashboard
      className={style.cardDashboardNoMarginBottom}
      title={t('typography.disbursementsBreakdown')}
      action={
        <Flex gap={8}>
          <DisbursementsBreakdownFilters
            organisationFilter={organisationFilter}
            statusFilter={statusFilter}
            beneficiaryStatusFilter={showBeneficiaryUI ? beneficiaryStatusFilter : undefined}
          />
          <Export
            id={disbursementBatchId}
            foundationId={foundationId}
            endDate={endDate}
            disbursementBatchId={disbursementBatchId}
            detailedCounts={detailedCounts}
            summary={summary}
          />
          {batchApprovalCta && (
            <Tooltip content={getBatchApprovalDisabledTooltip()}>
              <span>
                <Button
                  variant="secondary"
                  size="small"
                  onClick={batchApprovalCta.open}
                  disabled={!batchApprovalCta.canPerform || isDisbursementApprovalDisabled}
                  data-testid={batchApprovalCta.dataTestId}
                >
                  {batchApprovalCta.label}
                </Button>
              </span>
            </Tooltip>
          )}
          {shouldShowBatchPaymentFile && (
            <Button
              variant="secondary"
              size="small"
              onClick={() => setIsMarkAllChequesSentModalOpen(true)}
              disabled={!canMarkAllChequesSent}
            >
              {t('button.markApprovedAsSent')}
            </Button>
          )}
        </Flex>
      }
    >
      {queryParams.organisationId?.length && (
        <Chip
          label={t('typography.organisation')}
          value={organisationFilter.selectedOrganisationOptions.map(organisation => organisation.label).join(', ')}
          onClick={() => setQueryParams({ ...queryParams, organisationId: [] })}
          testId="organisation-id-filter-chip"
        />
      )}
      {!!queryParams.status?.length && (
        <Chip
          label={t('typography.status')}
          value={statusFilter.statusesOptions
            .filter(option => queryParams.status?.includes(option.value))
            .map(option => option.label)
            .join(', ')}
          onClick={() => setQueryParams({ ...queryParams, status: [] })}
          testId="status-filter-chip"
        />
      )}
      {showBeneficiaryUI && !!queryParams.beneficiaryStatus?.length && (
        <Chip
          label={t('table.beneficiary')}
          value={beneficiaryStatusFilter.selectedBeneficiaryStatuses
            .map(value => beneficiaryStatusFilter.beneficiaryFilterLabelsByValue[value] ?? value)
            .join(', ')}
          onClick={() => setQueryParams({ ...queryParams, beneficiaryStatus: [] })}
          testId="beneficiary-status-filter-chip"
        />
      )}
      <div ref={tableContainerRef}>
        <TableProvider
          className={style.tableProvider}
          columns={columns}
          totalResults={totalResults}
          isLoading={false}
          illustration="no-results"
          emptyTableTitle={t('table.emptyDisbursementsBreakdown')}
          emptyTableDescription=""
          dataTestId="disbursementsTable"
          sortByColumn={queryParams.sort ?? undefined}
          sortDirection={(queryParams.direction?.toUpperCase() as TableColumnSortDirection) ?? undefined}
          handleRequestSort={handleRequestSort}
        >
          <Table>
            <TableBody>
              {data.map(row => {
                const openActionModal = (kind: DisbursementActionModalKind) => {
                  setSelectedDisbursementDetails(row)
                  setActiveActionModal(kind)
                }

                const handleCancel =
                  row.status === DisbursementStatus.RETURNED ||
                  (row.status !== DisbursementStatus.PAID && row.status !== DisbursementStatus.CANCELLED)
                    ? () => openActionModal(DisbursementActionModalKind.CANCEL)
                    : undefined
                const handleMarkAsPaid =
                  (row.status === DisbursementStatus.PENDING_PAYMENT && row.type !== DisbursementType.CHEQUE) ||
                  (row.status === DisbursementStatus.SENT && row.type === DisbursementType.CHEQUE) ||
                  (row.status === DisbursementStatus.RETURNED && row.type === DisbursementType.INTERNAL)
                    ? () => openActionModal(DisbursementActionModalKind.MARK_AS_PAID)
                    : undefined
                const handleMarkAsSent =
                  row.status === DisbursementStatus.PENDING_PAYMENT && row.type === DisbursementType.CHEQUE
                    ? () => openActionModal(DisbursementActionModalKind.MARK_AS_SENT)
                    : undefined
                const handleMarkAsApproved =
                  row.status === DisbursementStatus.PENDING_APPROVAL
                    ? () => openActionModal(DisbursementActionModalKind.MARK_AS_APPROVED)
                    : undefined
                const handleReturnPayment =
                  row.status === DisbursementStatus.PAID && row.type === DisbursementType.INTERNAL
                    ? () => openActionModal(DisbursementActionModalKind.RETURNED_PAYMENT)
                    : undefined
                const handleConfirmBeneficiary =
                  beneficiariesFeatureFlag &&
                  row.type === DisbursementType.INTERNAL &&
                  (row.beneficiary?.status === BeneficiaryStatus.PROCESSING ||
                    row.beneficiary?.status === BeneficiaryStatus.OUTDATED ||
                    !row.beneficiary)
                    ? () => openActionModal(DisbursementActionModalKind.CONFIRM_BENEFICIARY)
                    : undefined
                const handleViewBankDetails = row.bankDetailsReviewId
                  ? () =>
                      window.open(`/bank-details-reviews/${row.bankDetailsReviewId}`, '_blank', 'noopener,noreferrer')
                  : undefined
                const handleViewDonationsList = () =>
                  navigate(`/disbursements/${disbursementBatchId}/donations/${row.id}`)

                const isActionsVisible =
                  disbursementDonationsViewFeatureFlag ||
                  (!disbursementDonationsViewFeatureFlag &&
                    (row.status === DisbursementStatus.PENDING_PAYMENT ||
                      row.status === DisbursementStatus.PENDING_APPROVAL ||
                      row.status === DisbursementStatus.SENT))

                return (
                  <TableRow key={row.id}>
                    <TableCell>
                      <Checkbox
                        name={`selectDisbursement-${row.id}`}
                        label=""
                        variant="default"
                        active={selectedDisbursementIds.includes(row.id)}
                        onChange={() => handleToggleRow(row)}
                        disabled={getIsCheckboxDisabled(row)}
                        aria-label={`Select disbursement ${row.id}`}
                        dataTestId={`disbursements-row-checkbox-${row.id}`}
                      />
                    </TableCell>
                    <TableCell>
                      <FlagAvatar key={row.id} code3={row.organisationCountryCode} />
                    </TableCell>
                    <TableCell>
                      <Tooltip content={row.organisationName}>
                        <span className={style.truncatedTextWide}>{row.organisationName}</span>
                      </Tooltip>
                    </TableCell>
                    {disbursementsBatchPartnerFilterFeatureFlag ? (
                      <TableCell>
                        <Tooltip content={row.partnerName ?? t('status.notAvailable')}>
                          <span className={style.truncatedText}>{row.partnerName ?? t('status.notAvailable')}</span>
                        </Tooltip>
                      </TableCell>
                    ) : null}
                    <TableCell>
                      {formatAmountFromMinorUnits({
                        currencyCode: row.foundationCurrencyCode,
                        value: row.foundationAmount,
                        currencies: currencyInfo
                      })}
                    </TableCell>
                    <TableCell>{row.foundationCurrencyCode}</TableCell>
                    <TableCell>{getBadgeVariant(row.status)}</TableCell>
                    {showBeneficiaryUI && <TableCell>{getBeneficiaryBadge(row.beneficiary)}</TableCell>}
                    {logReturnedPaymentFeatureFlag && <TableCell>{row.paymentCount ?? 0}</TableCell>}
                    <TableCell>
                      {row.updatedAt ? dayJS(row.updatedAt).format('DD MMM YYYY, HH:mm') : t('status.notAvailable')}
                    </TableCell>
                    <TableCell className={style.menuBtnWrapper}>
                      {isActionsVisible ? (
                        <DisbursementsDetailsActionsMenu
                          handleViewDonationsList={handleViewDonationsList}
                          handleCancel={handleCancel}
                          handleMarkAsPaid={handleMarkAsPaid}
                          handleMarkAsSent={handleMarkAsSent}
                          handleMarkAsApproved={handleMarkAsApproved}
                          handleReturnPayment={handleReturnPayment}
                          handleConfirmBeneficiary={handleConfirmBeneficiary}
                          handleViewBankDetails={handleViewBankDetails}
                          disbursement={row}
                        />
                      ) : null}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
          <TablePagination
            totalResultsText={t('table.results', { count: totalResults ?? 0 })}
            previousPage={previousPage}
            nextPage={nextPage}
          />
        </TableProvider>
      </div>
      <BulkActionMenu
        selectedDisbursementIds={selectedDisbursementIds}
        data={data}
        onOpenBulkRemovalModal={() => setIsBulkRemovalModalOpen(true)}
        onOpenMarkAsApprovedBulkModal={() => setIsMarkAsApprovedBulkModalOpen(true)}
        onOpenMarkAsSentBulkModal={() => setIsMarkAsSentBulkModalOpen(true)}
        onSelectAllByStatus={handleSelectAllByStatus}
        tableContainerRef={tableContainerRef}
      />
      {isBulkRemovalModalOpen && (
        <RemoveFromBatchBulk
          isOpen={isBulkRemovalModalOpen}
          onClose={() => setIsBulkRemovalModalOpen(false)}
          selectedDisbursementIds={selectedDisbursementIds}
          data={data}
          refreshData={refreshData}
          onSelectedDisbursementIdsChange={setSelectedDisbursementIds}
        />
      )}
      {isMarkAsApprovedBulkModalOpen && (
        <MarkAsApprovedBulk
          isOpen={isMarkAsApprovedBulkModalOpen}
          onClose={() => setIsMarkAsApprovedBulkModalOpen(false)}
          selectedDisbursementIds={selectedDisbursementIds}
          data={data}
          refreshData={refreshData}
          onSelectedDisbursementIdsChange={setSelectedDisbursementIds}
        />
      )}
      {(isMarkAsSentBulkModalOpen || isMarkAllChequesSentModalOpen) && (
        <MarkAsSentBulk
          isOpen={isMarkAsSentBulkModalOpen || isMarkAllChequesSentModalOpen}
          onClose={() => {
            setIsMarkAsSentBulkModalOpen(false)
            setIsMarkAllChequesSentModalOpen(false)
          }}
          selectedDisbursementIds={selectedDisbursementIds}
          data={data}
          refreshData={refreshData}
          onSelectedDisbursementIdsChange={setSelectedDisbursementIds}
          disbursementBatchId={isMarkAllChequesSentModalOpen ? disbursementBatchId : undefined}
          approvedChequesCount={isMarkAllChequesSentModalOpen ? approvedChequesCount : undefined}
        />
      )}
      {selectedDisbursementDetails && activeActionModal ? (
        <>
          <MarkAsPaidModal
            isOpen={activeActionModal === DisbursementActionModalKind.MARK_AS_PAID}
            onClose={closeModal}
            disbursementDetails={selectedDisbursementDetails}
            refreshData={refreshData}
          />
          <LogReturnedPayment
            isOpen={activeActionModal === DisbursementActionModalKind.RETURNED_PAYMENT}
            onClose={closeModal}
            disbursement={selectedDisbursementDetails}
            currencyInfo={currencyInfo}
            refreshData={refreshData}
          />
          <MarkAsSentModal
            isOpen={activeActionModal === DisbursementActionModalKind.MARK_AS_SENT}
            onClose={closeModal}
            disbursementId={selectedDisbursementDetails.id}
            organisationName={selectedDisbursementDetails.organisationName}
            refreshData={refreshData}
          />
          <MarkAsApprovedOrRemoveFromBatchModal
            isOpen={
              activeActionModal === DisbursementActionModalKind.MARK_AS_APPROVED ||
              (!disbursementCancellationReasonFeatureFlag && activeActionModal === DisbursementActionModalKind.CANCEL)
            }
            onClose={closeModal}
            disbursementId={selectedDisbursementDetails.id}
            organisationName={selectedDisbursementDetails.organisationName}
            refreshData={refreshData}
            actionType={activeActionModal}
          />
          {disbursementCancellationReasonFeatureFlag && (
            <RemoveFromBatchModal
              isOpen={activeActionModal === DisbursementActionModalKind.CANCEL}
              onClose={closeModal}
              disbursement={selectedDisbursementDetails}
              refreshData={refreshData}
            />
          )}
          {beneficiariesFeatureFlag && (
            <ConfirmBeneficiaryModal
              isOpen={activeActionModal === DisbursementActionModalKind.CONFIRM_BENEFICIARY}
              onClose={closeModal}
              disbursement={selectedDisbursementDetails}
              refreshData={refreshData}
            />
          )}
        </>
      ) : null}
      {isTrusteeApproveBatchModalOpen && (
        <TrusteeApproveBatchModal
          isOpen={isTrusteeApproveBatchModalOpen}
          onClose={() => setIsTrusteeApproveBatchModalOpen(false)}
          disbursementBatchId={disbursementBatchId}
          refreshData={() => refreshData?.()}
        />
      )}
      {isAgentApproveBatchModalOpen && (
        <AgentApproveBatchModal
          isOpen={isAgentApproveBatchModalOpen}
          onClose={() => setIsAgentApproveBatchModalOpen(false)}
          disbursementBatchId={disbursementBatchId}
          refreshData={() => refreshData?.()}
        />
      )}
      {isManagerApproveBatchModalOpen && (
        <ManagerApproveBatchModal
          isOpen={isManagerApproveBatchModalOpen}
          onClose={() => setIsManagerApproveBatchModalOpen(false)}
          disbursementBatchId={disbursementBatchId}
          totalAmount={totals.managerApprovalRequired}
          totalAmountCurrencyCode={totals.currencyCode}
          totalDisbursementsCount={totalDisbursementsCount}
          allBeneficiariesConfirmed={allBeneficiariesConfirmed}
          refreshData={() => refreshData?.()}
        />
      )}
    </CardDashboard>
  )
}
